import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, MessageSquarePlus, Languages, Receipt } from "lucide-react";
import { Reveal } from "@/components/site/Motion";
import { useWaitlist } from "@/context/WaitlistContext";

const Featured = () => (
  <Link to="/tool/mimis-video" className="group relative flex h-full min-h-[440px] flex-col overflow-hidden rounded-[1.75rem] bg-[#111111] p-7 text-white sm:p-10 grain" data-testid="bento-featured-tool">
    <div className="absolute inset-y-6 right-6 hidden w-[38%] overflow-hidden rounded-[1.25rem] md:block">
      <img src="/media/creator.jpg" alt="Creatrice che registra un video verticale" className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:grayscale-0" />
      <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#111111]/50" />
    </div>
    <div className="relative z-10 flex flex-1 flex-col md:max-w-[56%]">
      <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold backdrop-blur">
        <span className="h-1.5 w-1.5 rounded-full bg-[#4d94ff]" /> Disponibile ora · Tool n.1
      </span>
      <h3 className="font-display mt-6 text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl">MIMIS Video<br /><span className="text-[#4d94ff]">Italia</span></h3>
      <p className="mt-5 max-w-md leading-relaxed text-zinc-300">Scrivi un'idea in italiano e in 60 secondi hai un video con voce, sottotitoli e immagini, pronto per TikTok e Instagram.</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {["Voce italiana", "Sottotitoli", "Formato 9:16"].map((c) => (
          <span key={c} className="rounded-full border border-white/15 px-3 py-1 text-xs text-zinc-200">{c}</span>
        ))}
      </div>
      <div className="mt-auto pt-10">
        <img src="/media/creator.jpg" alt="" className="mb-6 h-40 w-full rounded-2xl object-cover grayscale md:hidden" />
        <span className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#111111] transition-transform duration-300 group-hover:translate-x-1">
          Scopri il tool <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
        </span>
      </div>
    </div>
  </Link>
);

const Soon = () => (
  <div className="flex h-full min-h-[210px] flex-col justify-between rounded-[1.75rem] border border-dashed border-[#d4d4d8] bg-[#F5F5F7] p-7 transition-colors duration-300 hover:border-[#0066FF]/50" data-testid="bento-soon-tool">
    <span className="grid h-11 w-11 place-items-center rounded-xl bg-white text-[#52525b] shadow-sm"><Lock className="h-5 w-5" /></span>
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#52525b]">Tool n.2 · Prossimamente</p>
      <p className="font-display mt-2 text-xl font-bold tracking-tight">Il prossimo problema è già sul tavolo.</p>
    </div>
  </div>
);

const Propose = ({ onClick }) => (
  <button onClick={onClick} className="group flex h-full min-h-[210px] w-full flex-col justify-between rounded-[1.75rem] bg-[#0066FF] p-7 text-left text-white transition-transform duration-300 hover:-translate-y-1" data-testid="bento-propose-button">
    <span className="grid h-11 w-11 place-items-center rounded-xl bg-white/15"><MessageSquarePlus className="h-5 w-5" /></span>
    <div>
      <p className="font-display text-xl font-bold leading-snug tracking-tight">I prossimi tool nascono dai vostri problemi.</p>
      <p className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-white/90">Entra in lista e dicci la tua <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></p>
    </div>
  </button>
);

const Small = ({ icon: Icon, title, text, big, testId }) => (
  <div className="card-soft flex h-full items-center gap-6 p-7 sm:p-8" data-testid={testId}>
    <span className="font-display shrink-0 text-6xl font-extrabold tracking-tighter text-[#0066FF] sm:text-7xl">{big}</span>
    <div>
      <p className="flex items-center gap-2 font-display text-xl font-bold tracking-tight"><Icon className="h-5 w-5 text-[#0066FF]" /> {title}</p>
      <p className="mt-2 leading-relaxed text-[#52525b]">{text}</p>
    </div>
  </div>
);

export const ToolsBento = () => {
  const { openWaitlist } = useWaitlist();
  return (
    <section id="tool" className="bg-white py-24 sm:py-32" data-testid="tools-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="eyebrow">I Tool</span>
            <h2 className="font-display mt-4 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">Un hub.<br /><span className="text-[#a1a1aa]">Tanti problemi risolti.</span></h2>
          </div>
          <p className="max-w-sm text-base text-[#52525b] md:text-lg">Un tool per ogni problema. Semplici, in italiano, pensati per chi non ha tempo da perdere.</p>
        </Reveal>
        <div className="mt-14 grid gap-4 md:grid-cols-6 md:grid-rows-[auto_auto_auto]">
          <Reveal className="md:col-span-4 md:row-span-2"><Featured /></Reveal>
          <Reveal className="md:col-span-2" delay={0.1}><Soon /></Reveal>
          <Reveal className="md:col-span-2" delay={0.18}><Propose onClick={() => openWaitlist("proposta")} /></Reveal>
          <Reveal className="md:col-span-3" delay={0.05}>
            <Small icon={Languages} big="IT" title="100% in italiano" text="Interfaccia, voci e testi pensati per chi parla italiano." testId="bento-italian" />
          </Reveal>
          <Reveal className="md:col-span-3" delay={0.12}>
            <Small icon={Receipt} big="€" title="Prezzi chiari" text="Un abbonamento mensile per tool. Nessun costo nascosto." testId="bento-pricing" />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
